// src/pages/stock/StockSortieCreate.js - VERSION AVEC API SÉPARÉE POUR CONTRÔLEURS
import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import Layout from "../../components/Layout";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost/securecontrol-backend";

// Palette de couleurs SecureControl
const COLORS = {
  primary: '#0B3C5D',
  secondary: '#1E88C8',
  accent: '#4FC3F7',
  darkText: '#2E2E2E',
  lightBg: '#F5F7FA',
  white: '#FFFFFF',
  danger: '#f44336',
  success: '#4caf50',
  warning: '#ff9800',
  info: '#2196f3',
  successLight: '#e8f5e9',
  dangerLight: '#ffebee',
  warningLight: '#fff3e0',
  infoLight: '#e3f2fd'
};

const SortirStockCreate = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dropdownRef = useRef(null);
  const incidentDropdownRef = useRef(null);
  
  // États du formulaire
  const [formData, setFormData] = useState({
    id_produit: "",
    quantite_sortie_stock: "",
    motif_sortie_stock: "",
    destination_type: "",
    destination_id: "",
    id_incident: ""
  });
  
  // États pour l'UI
  const [loading, setLoading] = useState(false);
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [loadingDestinations, setLoadingDestinations] = useState(false);
  const [loadingIncidents, setLoadingIncidents] = useState(false);
  const [serverError, setServerError] = useState("");
  const [infoMsg, setInfoMsg] = useState("");
  const [validationErrors, setValidationErrors] = useState([]);
  
  // États pour l'erreur d'abonnement
  const [abonnementError, setAbonnementError] = useState(false);
  const [abonnementMessage, setAbonnementMessage] = useState("");
  
  // États pour les données
  const [produits, setProduits] = useState([]);
  const [filteredProduits, setFilteredProduits] = useState([]);
  const [produitSearch, setProduitSearch] = useState("");
  const [showProduitDropdown, setShowProduitDropdown] = useState(false);
  
  // États pour les incidents
  const [incidents, setIncidents] = useState([]);
  const [filteredIncidents, setFilteredIncidents] = useState([]);
  const [incidentSearch, setIncidentSearch] = useState("");
  const [showIncidentDropdown, setShowIncidentDropdown] = useState(false);
  
  const [destinations, setDestinations] = useState([]);
  
  // Informations utilisateur courant
  const [currentUser, setCurrentUser] = useState(null);
  
  // Stock actuel du produit sélectionné
  const [currentStock, setCurrentStock] = useState(0);
  
  // Vérifier si l'utilisateur a le bon profil (admin ou gestionnaire)
  const [hasPermission, setHasPermission] = useState(true);
  
  // Types de destination autorisés
  const destinationTypes = [
    { value: 'site', label: 'Site' },
    { value: 'agent', label: 'Agent' },
    { value: 'controleur', label: 'Contrôleur' },
    { value: 'self', label: 'Moi-même' }
  ];
  
  // Fonction pour réinitialiser l'erreur d'abonnement
  const resetAbonnementError = () => {
    setAbonnementError(false);
    setAbonnementMessage("");
  };
  
  // Fonction pour charger les produits actifs depuis l'API
  const loadProduits = async () => {
    try {
      const token = localStorage.getItem("access_token");
      if (!token) {
        setServerError("Token d'authentification manquant.");
        setLoadingProducts(false);
        return;
      }
      
      const response = await axios.get(`${API_BASE_URL}/stock/produits/list_produit.php`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      
      console.log("Produits chargés:", response.data);
      
      if (response.data?.success && response.data?.data?.produits) {
        const produitsActifs = response.data.data.produits
          .filter(produit => parseInt(produit.statut) === 1 && parseInt(produit.quantite_stock) > 0)
          .map(produit => ({
            value: produit.id.toString(),
            label: `${produit.libele} (Stock: ${produit.quantite_stock})`,
            libele: produit.libele,
            stock: parseInt(produit.quantite_stock),
            id: produit.id
          }));
        
        setProduits(produitsActifs);
        setFilteredProduits(produitsActifs);
        
        // Pré-remplissage depuis l'URL
        const searchParams = new URLSearchParams(location.search);
        const produitId = searchParams.get('produit');
        
        if (produitId && produitsActifs.length > 0) {
          const produitTrouve = produitsActifs.find(p => 
            p.id.toString() === produitId || 
            p.value === produitId
          );
          
          if (produitTrouve) {
            setFormData(prev => ({
              ...prev,
              id_produit: produitTrouve.value,
            }));
            setProduitSearch(produitTrouve.libele);
            setCurrentStock(produitTrouve.stock);
          } else {
            setServerError(`Le produit sélectionné (ID: ${produitId}) n'est pas disponible.`);
          }
        }
        
        if (produitsActifs.length === 0) {
          setServerError("Aucun produit actif avec stock disponible.");
        }
      } else {
        setServerError("Erreur lors du chargement des produits.");
      }
    } catch (err) {
      console.error("Erreur chargement produits:", err);
      
      // Vérifier si c'est une erreur 403 avec code ABONNEMENT_REQUIS
      if (err.response?.status === 403 && err.response?.data?.code === 'ABONNEMENT_REQUIS') {
        setAbonnementError(true);
        setAbonnementMessage(err.response.data.message || "Abonnement inactif");
        setProduits([]);
        setFilteredProduits([]);
      } else if (err.response?.status === 401) {
        setServerError("Session expirée. Veuillez vous reconnecter.");
        setTimeout(() => navigate("/login"), 2000);
      } else if (err.response?.status === 403) {
        setServerError("Permission refusée pour accéder aux produits.");
      } else if (err.response?.status === 500) {
        setServerError("Erreur serveur lors du chargement des produits.");
      } else {
        setServerError("Erreur réseau lors du chargement des produits.");
      }
    } finally {
      setLoadingProducts(false);
    }
  };
  
  // Fonction pour charger seulement les incidents NON RÉSOLUS depuis l'API
  const loadIncidents = async () => {
    try {
      const token = localStorage.getItem("access_token");
      if (!token) {
        console.error("Token manquant pour charger les incidents");
        return;
      }
      
      setLoadingIncidents(true);
      
      const response = await axios.get(`${API_BASE_URL}/incidents/liste_incidents.php`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      
      console.log("Tous les incidents chargés:", response.data);
      
      if (response.data?.success && response.data?.data) {
        const incidentsNonResolus = response.data.data.filter(incident => {
          const dateResolus = incident.date_resolus_incident;
          
          if (!dateResolus) return true;
          if (dateResolus.trim() === "") return true;
          if (dateResolus === "0000-00-00" || dateResolus === "0000-00-00 00:00:00") return true;
          
          return false;
        });
        
        console.log(`${incidentsNonResolus.length} incidents non résolus trouvés`);
        
        const incidentsList = incidentsNonResolus.map(incident => {
          return {
            value: incident.id_incident.toString(),
            label: `Ticket ${incident.numero_ticket_incident} - ${incident.description_incident}`,
            searchText: `Ticket ${incident.numero_ticket_incident} ${incident.description_incident}`.toLowerCase(),
            numero_ticket: incident.numero_ticket_incident,
            description: incident.description_incident,
            statut: incident.statut,
            date_resolus_incident: incident.date_resolus_incident
          };
        });
        
        setIncidents(incidentsList);
        setFilteredIncidents(incidentsList);
        
        console.log(`${incidentsList.length} incidents non résolus chargés`);
      } else {
        console.error("Format de réponse inattendu pour les incidents:", response.data);
      }
    } catch (err) {
      console.error("Erreur chargement incidents:", err);
      
      // Vérifier si c'est une erreur 403 avec code ABONNEMENT_REQUIS
      if (err.response?.status === 403 && err.response?.data?.code === 'ABONNEMENT_REQUIS') {
        setAbonnementError(true);
        setAbonnementMessage(err.response.data.message || "Abonnement inactif");
        setIncidents([]);
        setFilteredIncidents([]);
      }
    } finally {
      setLoadingIncidents(false);
    }
  };
  
  // Fonction pour charger les destinations selon le type - CORRIGÉE AVEC API SÉPARÉE POUR CONTRÔLEURS
  const loadDestinations = async (type) => {
    if (!type || type === 'self') {
      setDestinations([]);
      setLoadingDestinations(false);
      return;
    }
    
    setLoadingDestinations(true);
    setDestinations([]);
    
    try {
      const token = localStorage.getItem("access_token");
      if (!token) {
        setServerError("Token d'authentification manquant.");
        setLoadingDestinations(false);
        return;
      }
      
      let url = "";
      let isApiSeparate = false;
      
      switch(type) {
        case 'site':
          url = `${API_BASE_URL}/sites/list.php`;
          break;
        case 'agent':
          url = `${API_BASE_URL}/agents/list.php`;
          break;
        case 'controleur':
          url = `${API_BASE_URL}/users/list.php?profile=2`;
          isApiSeparate = true;
          break;
        default:
          setLoadingDestinations(false);
          return;
      }
      
      console.log(`Chargement destinations ${type} depuis: ${url}`);
      
      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      
      console.log(`Réponse API pour ${type}:`, response.data);
      
      let destinationsList = [];
      
      if (type === 'site') {
        // Pour les sites
        let sitesData = [];
        
        if (response.data?.data?.sites) {
          sitesData = response.data.data.sites;
        } else if (response.data?.sites) {
          sitesData = response.data.sites;
        } else if (Array.isArray(response.data?.data)) {
          sitesData = response.data.data;
        } else if (Array.isArray(response.data)) {
          sitesData = response.data;
        }
        
        destinationsList = sitesData.map(item => {
          const id = item.id_site || item.id || item.value || '';
          const nom = item.nom_site || item.nom || item.label || item.name || 'Site sans nom';
          
          return {
            value: id.toString(),
            label: nom,
            originalData: item
          };
        }).filter(item => item.value && item.value !== '');
        
      } else if (type === 'agent') {
        // Pour les agents - API agents/list.php
        let agentsData = [];
        
        // Votre API agents renvoie les données dans response.data.agents
        if (response.data?.agents && Array.isArray(response.data.agents)) {
          agentsData = response.data.agents;
        } else if (response.data?.data?.agents) {
          agentsData = response.data.data.agents;
        } else if (Array.isArray(response.data?.data)) {
          agentsData = response.data.data;
        } else if (Array.isArray(response.data)) {
          agentsData = response.data;
        }
        
        console.log(`Données agents extraites:`, agentsData);
        
        destinationsList = agentsData.map(agent => {
          const id = agent.id_agent || agent.id || agent.value || '';
          const nom = agent.nom_agent || agent.nom || agent.nom_user || 'Sans nom';
          const prenom = agent.prenom_agent || agent.prenom || agent.prenom_user || '';
          const fullName = prenom ? `${nom} ${prenom}` : nom;
          
          return {
            value: id.toString(),
            label: `${fullName} (Agent)`,
            originalData: agent
          };
        }).filter(item => item.value && item.value !== '');
        
      } else if (type === 'controleur') {
        // Pour les contrôleurs - API user/list.php?profile=2
        let controleursData = [];
        
        // L'API user renvoie probablement les données dans response.data.users ou response.data
        if (response.data?.users && Array.isArray(response.data.users)) {
          controleursData = response.data.users;
        } else if (response.data?.data?.users) {
          controleursData = response.data.data.users;
        } else if (Array.isArray(response.data?.data)) {
          controleursData = response.data.data;
        } else if (Array.isArray(response.data)) {
          controleursData = response.data;
        }
        
        console.log(`Données contrôleurs extraites:`, controleursData);
        
        destinationsList = controleursData.map(controleur => {
          const id = controleur.id_user || controleur.id || controleur.value || '';
          const nom = controleur.nom_user || controleur.nom || 'Sans nom';
          const prenom = controleur.prenom_user || controleur.prenom || '';
          const fullName = prenom ? `${nom} ${prenom}` : nom;
          
          return {
            value: id.toString(),
            label: `${fullName} (Contrôleur)`,
            originalData: controleur
          };
        }).filter(item => item.value && item.value !== '');
      }
      
      console.log(`Destinations ${type} traitées:`, destinationsList);
      setDestinations(destinationsList);
      
      if (destinationsList.length === 0) {
        console.warn(`Aucune destination disponible pour le type "${type}"`);
      }
    } catch (err) {
      console.error(`Erreur chargement destinations ${type}:`, err);
      
      // Vérifier si c'est une erreur 403 avec code ABONNEMENT_REQUIS
      if (err.response?.status === 403 && err.response?.data?.code === 'ABONNEMENT_REQUIS') {
        setAbonnementError(true);
        setAbonnementMessage(err.response.data.message || "Abonnement inactif");
        setDestinations([]);
      } else if (err.response?.status === 401) {
        setServerError("Session expirée. Veuillez vous reconnecter.");
        setTimeout(() => navigate("/login"), 2000);
      } else if (err.response?.status === 403) {
        setServerError(`Permission refusée pour accéder aux ${type}s.`);
      } else if (err.response?.status === 404) {
        setServerError(`API non trouvée pour les ${type}s.`);
      } else {
        setServerError(`Erreur réseau lors du chargement des ${type}s.`);
      }
    } finally {
      setLoadingDestinations(false);
    }
  };
  
  // Récupérer les informations de l'utilisateur courant
  const getCurrentUserInfo = () => {
    const token = localStorage.getItem("access_token");
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        return {
          id: payload.id_user,
          profile: payload.profile,
          nom: payload.nom || "Utilisateur",
          prenom: payload.prenom || ""
        };
      } catch (err) {
        console.error("Erreur décodage token:", err);
      }
    }
    return null;
  };
  
  useEffect(() => {
    // Vérifier le profil utilisateur
    const token = localStorage.getItem("access_token");
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        const userProfile = payload.profile;
        
        // Stocker les infos utilisateur
        setCurrentUser({
          id: payload.id_user,
          profile: userProfile,
          nom: payload.nom || "Utilisateur",
          prenom: payload.prenom || ""
        });
        
        if (![1, 3].includes(userProfile)) {
          setHasPermission(false);
          setServerError("Permission refusée. Seuls les administrateurs et gestionnaires peuvent effectuer une sortie de stock.");
        } else {
          // Si l'utilisateur a la permission, charger les données
          loadProduits();
          loadIncidents();
        }
      } catch (err) {
        console.error("Erreur décodage token:", err);
        setLoadingProducts(false);
      }
    } else {
      setServerError("Non authentifié. Veuillez vous connecter.");
      setLoadingProducts(false);
    }
  }, []);
  
  // Effet pour filtrer les produits quand la recherche change
  useEffect(() => {
    if (!produitSearch.trim()) {
      setFilteredProduits(produits);
    } else {
      const searchTerm = produitSearch.toLowerCase();
      const filtered = produits.filter(produit => 
        produit.libele.toLowerCase().includes(searchTerm) ||
        produit.label.toLowerCase().includes(searchTerm)
      );
      setFilteredProduits(filtered);
    }
  }, [produitSearch, produits]);
  
  // Effet pour filtrer les incidents quand la recherche change
  useEffect(() => {
    if (!incidentSearch.trim()) {
      setFilteredIncidents(incidents);
    } else {
      const searchTerm = incidentSearch.toLowerCase();
      const filtered = incidents.filter(incident => 
        incident.searchText.includes(searchTerm)
      );
      setFilteredIncidents(filtered);
    }
  }, [incidentSearch, incidents]);
  
  // Effet pour charger les destinations quand le type change
  useEffect(() => {
    if (formData.destination_type) {
      loadDestinations(formData.destination_type);
      
      if (formData.destination_type === 'self') {
        setFormData(prev => ({
          ...prev,
          destination_id: ""
        }));
      }
    } else {
      setDestinations([]);
    }
  }, [formData.destination_type]);
  
  // Effet pour mettre à jour le stock quand le produit change
  useEffect(() => {
    if (formData.id_produit) {
      const selectedProduit = produits.find(p => p.value === formData.id_produit);
      if (selectedProduit) {
        setCurrentStock(selectedProduit.stock);
      }
    } else {
      setCurrentStock(0);
    }
  }, [formData.id_produit, produits]);
  
  // Fermer les dropdowns quand on clique en dehors
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowProduitDropdown(false);
      }
      if (incidentDropdownRef.current && !incidentDropdownRef.current.contains(event.target)) {
        setShowIncidentDropdown(false);
      }
    };
    
    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, []);
  
  // Gérer la sélection d'un produit
  const handleProduitSelect = (produitValue, produitLabel) => {
    setFormData({
      ...formData,
      id_produit: produitValue
    });
    setProduitSearch(produits.find(p => p.value === produitValue)?.libele || produitLabel);
    setShowProduitDropdown(false);
  };
  
  // Gérer la recherche de produit
  const handleProduitSearchChange = (e) => {
    const value = e.target.value;
    setProduitSearch(value);
    
    if (!value.trim()) {
      setFormData({
        ...formData,
        id_produit: ""
      });
    }
    
    if (value && !showProduitDropdown) {
      setShowProduitDropdown(true);
    }
  };
  
  // Gérer la sélection d'un incident
  const handleIncidentSelect = (incidentValue, incidentLabel) => {
    setFormData({
      ...formData,
      id_incident: incidentValue
    });
    setIncidentSearch(incidents.find(i => i.value === incidentValue)?.label || incidentLabel);
    setShowIncidentDropdown(false);
  };
  
  // Gérer la recherche d'incident
  const handleIncidentSearchChange = (e) => {
    const value = e.target.value;
    setIncidentSearch(value);
    
    if (!value.trim()) {
      setFormData({
        ...formData,
        id_incident: ""
      });
    }
    
    if (value && !showIncidentDropdown) {
      setShowIncidentDropdown(true);
    }
  };
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    
    let cleanedValue = value;
    
    if (name === "quantite_sortie_stock") {
      cleanedValue = value.replace(/\D/g, "");
      const numValue = parseInt(cleanedValue);
      if (numValue > currentStock) {
        cleanedValue = currentStock.toString();
      }
    }
    
    setFormData({
      ...formData,
      [name]: cleanedValue
    });
    
    if (validationErrors.length > 0) {
      setValidationErrors([]);
    }
  };
  
  const validateForm = () => {
    const errors = [];
    
    if (!formData.id_produit || formData.id_produit === "") {
      errors.push("La sélection d'un produit est obligatoire.");
    }
    
    const quantite = parseInt(formData.quantite_sortie_stock);
    if (isNaN(quantite) || quantite <= 0) {
      errors.push("La quantité doit être supérieure à 0.");
    } else if (quantite > currentStock) {
      errors.push(`Quantité insuffisante. Stock disponible: ${currentStock}`);
    }
    
    if (!formData.motif_sortie_stock || formData.motif_sortie_stock.trim() === "") {
      errors.push("Le motif de sortie est obligatoire.");
    } else if (formData.motif_sortie_stock.trim().length > 255) {
      errors.push("Le motif ne peut pas dépasser 255 caractères.");
    }
    
    if (!formData.destination_type || formData.destination_type === "") {
      errors.push("Le type de destination est obligatoire.");
    }
    
    if (formData.destination_type !== 'self' && (!formData.destination_id || formData.destination_id === "")) {
      let label = "";
      switch(formData.destination_type) {
        case 'site': label = "un site"; break;
        case 'agent': label = "un agent"; break;
        case 'controleur': label = "un contrôleur"; break;
        default: label = formData.destination_type;
      }
      errors.push(`L'ID de destination est obligatoire pour ${label}.`);
    }
    
    return errors;
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Réinitialiser les messages
    setServerError("");
    setValidationErrors([]);
    resetAbonnementError();
    
    const errors = validateForm();
    if (errors.length > 0) {
      setValidationErrors(errors);
      setServerError("Veuillez corriger les erreurs dans le formulaire.");
      return;
    }
    
    // Préparer les données pour l'API
    const submitData = {
      id_produit: parseInt(formData.id_produit),
      quantite_sortie_stock: parseInt(formData.quantite_sortie_stock),
      motif_sortie_stock: formData.motif_sortie_stock.trim(),
      destination_type: formData.destination_type
    };
    
    // Ajouter destination_id seulement si différent de 'self'
    if (formData.destination_type !== 'self' && formData.destination_id) {
      submitData.destination_id = parseInt(formData.destination_id);
    }
    
    // Ajouter incident si sélectionné
    if (formData.id_incident) {
      submitData.id_incident = parseInt(formData.id_incident);
    }

    setLoading(true);
    
    try {
      const url = `${API_BASE_URL}/stock/sortir_stock/create_sortie.php`;
      console.log("Envoi des données sortie:", submitData);
      
      const response = await axios.post(url, submitData, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          "Content-Type": "application/json",
        },
      });
      
      console.log("Réponse création sortie stock:", response.data);
      
      if (response.data?.success) {
        const message = response.data.message || "Sortie de stock effectuée avec succès !";
        setInfoMsg(message);
        
        if (response.data.data?.sortie_stock?.destination) {
          const dest = response.data.data.sortie_stock.destination;
          let destInfo = "";
          if (dest.type === 'self') {
            const profileLabel = dest.profile_label || (dest.profile === 1 ? 'administrateur' : 'gestionnaire');
            destInfo = ` (Pour ${profileLabel}: ${dest.nom} ${dest.prenom})`;
          } else {
            destInfo = ` (Pour ${dest.type}: ${dest.nom} ${dest.prenom || ''})`;
          }
          setInfoMsg(prev => prev + destInfo);
        }
        
        setTimeout(() => {
          navigate("/products");
        }, 1000);
        
      } else {
        setServerError(
          response.data?.message ||
          "Impossible d'effectuer la sortie de stock. Veuillez réessayer."
        );
        
        if (response.data?.errors) {
          setValidationErrors(response.data.errors);
        }
      }
    } catch (err) {
      console.error("Erreur création sortie stock:", err);
      
      // Vérifier si c'est une erreur 403 avec code ABONNEMENT_REQUIS
      if (err.response?.status === 403 && err.response?.data?.code === 'ABONNEMENT_REQUIS') {
        setAbonnementError(true);
        setAbonnementMessage(err.response.data.message || "Abonnement inactif");
        setLoading(false);
        return;
      }
      
      if (err.response?.status === 400) {
        if (err.response.data?.errors) {
          setValidationErrors(err.response.data.errors);
          setServerError("Erreurs de validation");
        } else {
          setServerError(err.response.data?.message || "Données invalides.");
        }
      } else if (err.response?.status === 403) {
        setHasPermission(false);
        setServerError(err.response.data?.message || "Permission refusée.");
      } else if (err.response?.status === 404) {
        setServerError(err.response.data?.message || "Ressource introuvable.");
      } else if (err.response?.status === 401) {
        setServerError("Session expirée. Veuillez vous reconnecter.");
        setTimeout(() => navigate("/login"), 2000);
      } else if (err.response?.status === 500) {
        setServerError("Erreur serveur. Veuillez réessayer plus tard.");
      } else {
        setServerError("Erreur réseau. Vérifiez votre connexion.");
      }
    } finally {
      setLoading(false);
    }
  };
  
  const handleCancel = () => {
    if (window.confirm("Voulez-vous annuler la sortie ? Les modifications seront perdues.")) {
      navigate("/products");
    }
  };
  
  // Obtenir le produit sélectionné
  const selectedProduit = produits.find(p => p.value === formData.id_produit);
  
  // Obtenir l'incident sélectionné
  const selectedIncident = incidents.find(i => i.value === formData.id_incident);
  
  // Calculer le nouveau stock après sortie
  const quantiteSortie = parseInt(formData.quantite_sortie_stock) || 0;
  const nouveauStock = currentStock - quantiteSortie;
  
  // Si l'utilisateur n'a pas la permission
  if (!hasPermission) {
    return (
      <Layout>
        <div className="pagetitle">
          <h1 style={{ color: COLORS.primary, fontWeight: '600' }}>Gestion de stocks</h1>
          <nav>
            <ol className="breadcrumb">
              <li className="breadcrumb-item">
                <a href="/dashboard" style={{ color: COLORS.primary }}>Tableau de bord</a>
              </li>
              <li className="breadcrumb-item">
                <a href="/products" style={{ color: COLORS.primary }}>Produits</a>
              </li>
              <li className="breadcrumb-item active" style={{ color: COLORS.secondary }}>
                Nouvelle sortie de stock
              </li>
            </ol>
          </nav>
        </div>
        <section className="section">
          <div className="card" style={{ 
            borderRadius: '12px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
            border: 'none'
          }}>
            <div className="card-body text-center py-5">
              <div className="alert alert-dismissible fade show" style={{ 
                backgroundColor: '#ffebee',
                borderColor: COLORS.danger,
                color: '#c62828',
                borderRadius: '8px',
                borderLeft: `4px solid ${COLORS.danger}`,
                maxWidth: '600px',
                margin: '0 auto'
              }}>
                <div className="d-flex align-items-center">
                  <i className="bi bi-shield-exclamation me-2 fs-4"></i>
                  <div className="flex-grow-1">{serverError}</div>
                </div>
              </div>
              <button
                className="btn fw-semibold mt-4"
                onClick={() => navigate("/products")}
                style={{
                  color: COLORS.white,
                  backgroundColor: COLORS.primary,
                  borderColor: COLORS.primary,
                  borderRadius: '8px',
                  padding: '10px 24px',
                  minWidth: '200px',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#0A3250';
                  e.target.style.borderColor = '#0A3250';
                  e.target.style.transform = 'translateY(-2px)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = COLORS.primary;
                  e.target.style.borderColor = COLORS.primary;
                  e.target.style.transform = 'translateY(0)';
                }}
              >
                <i className="bi bi-arrow-left me-2"></i>
                Retour à la liste
              </button>
            </div>
          </div>
        </section>
      </Layout>
    );
  }
  
  return (
    <Layout>
      <div className="pagetitle">
        <h1 style={{ color: COLORS.primary, fontWeight: '600' }}>Gestion de stocks</h1>
        <nav>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <a href="/dashboard" style={{ color: COLORS.primary }}>Tableau de bord</a>
            </li>
            <li className="breadcrumb-item">
              <a href="/products" style={{ color: COLORS.primary }}>Produits</a>
            </li>
            <li className="breadcrumb-item active" style={{ color: COLORS.secondary }}>
              Nouvelle sortie de stock
            </li>
          </ol>
        </nav>
      </div>

      <section className="section">
        <div className="card" style={{ 
          borderRadius: '12px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
          border: 'none'
        }}>
          <div className="card-body">
            <div className="d-flex justify-content-between align-items-center">
              <div className="d-flex justify-content-between align-items-center mb-4">
                  <div>
                    <h5 className="card-title mb-1" style={{ 
                      color: COLORS.primary,
                      paddingBottom: '16px',
                      width: '100%'
                    }}>
                      <div className="d-flex align-items-center">
                        <div className="rounded-circle d-inline-flex align-items-center justify-content-center me-3" style={{ 
                          width: '36px',
                          height: '36px',
                          backgroundColor: '#E3F2FD',
                          color: COLORS.danger
                        }}>
                           <i className="bi bi-box-arrow-up"></i>
                        </div>
                        Nouvelle sortie de stock
                      </div>
                    </h5>
                  </div>
             </div>
          </div>

            {/* Affichage de l'erreur d'abonnement */}
            {abonnementError && (
              <div className="alert alert-warning alert-dismissible fade show mb-4" style={{ 
                backgroundColor: '#fff3e0',
                borderColor: COLORS.warning,
                color: '#ff8f00',
                borderRadius: '8px',
                borderLeft: `4px solid ${COLORS.warning}`,
                whiteSpace: "pre-line"
              }}>
                <div className="d-flex align-items-start">
                  <i className="bi bi-exclamation-triangle me-2 mt-1"></i>
                  <div className="flex-grow-1">
                    {abonnementMessage}
                    <div className="d-flex gap-2 justify-content-center mt-3">
                      <a 
                        href="/abonnements/create"
                        className="btn fw-semibold"
                        style={{
                          backgroundColor: '#4caf50',
                          color: 'white',
                          border: 'none',
                          borderRadius: '8px',
                          padding: '8px 20px',
                          textDecoration: 'none',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '8px'
                        }}
                        onMouseEnter={(e) => e.target.style.backgroundColor = '#45a049'}
                        onMouseLeave={(e) => e.target.style.backgroundColor = '#4caf50'}
                      >
                        <i className="bi bi-box-arrow-up-right"></i>
                        Souscrire à un abonnement
                      </a>
                      <button
                        className="btn fw-semibold"
                        onClick={() => {
                          setAbonnementError(false);
                          setAbonnementMessage("");
                        }}
                        style={{
                          backgroundColor: '#f44336',
                          color: 'white',
                          border: 'none',
                          borderRadius: '8px',
                          padding: '8px 20px'
                        }}
                        onMouseEnter={(e) => e.target.style.backgroundColor = '#d32f2f'}
                        onMouseLeave={(e) => e.target.style.backgroundColor = '#f44336'}
                      >
                        Fermer
                      </button>
                    </div>
                  </div>
                  <button 
                    type="button" 
                    className="btn-close" 
                    onClick={() => {
                      setAbonnementError(false);
                      setAbonnementMessage("");
                    }}
                  ></button>
                </div>
              </div>
            )}

            {/* Messages d'erreur/succès */}
            {serverError && !abonnementError && !validationErrors.length && (
              <div className="alert alert-dismissible fade show mb-4" style={{ 
                backgroundColor: '#ffebee',
                borderColor: COLORS.danger,
                color: '#c62828',
                borderRadius: '8px',
                borderLeft: `4px solid ${COLORS.danger}`
              }}>
                <div className="d-flex align-items-center">
                  <i className="bi bi-exclamation-triangle me-2"></i>
                  <div className="flex-grow-1">{serverError}</div>
                  <button 
                    type="button" 
                    className="btn-close" 
                    onClick={() => setServerError("")}
                  ></button>
                </div>
              </div>
            )}
            
            {validationErrors.length > 0 && (
              <div className="alert alert-warning alert-dismissible fade show mb-4" style={{ 
                backgroundColor: '#fff3e0',
                borderColor: COLORS.warning,
                color: '#f57c00',
                borderRadius: '8px',
                borderLeft: `4px solid ${COLORS.warning}`
              }}>
                <div className="d-flex align-items-start">
                  <i className="bi bi-exclamation-triangle me-2 mt-1"></i>
                  <div className="flex-grow-1">
                    <h6 className="alert-heading mb-2" style={{ color: '#e65100' }}>
                      Erreurs de validation
                    </h6>
                    <ul className="mb-0 ps-3" style={{ fontSize: '0.9rem' }}>
                      {validationErrors.map((error, index) => (
                        <li key={index} style={{ marginBottom: '4px' }}>{error}</li>
                      ))}
                    </ul>
                  </div>
                  <button 
                    type="button" 
                    className="btn-close" 
                    onClick={() => setValidationErrors([])}
                  ></button>
                </div>
              </div>
            )}
            
            {infoMsg && (
              <div className="alert alert-success alert-dismissible fade show mb-4" style={{ 
                backgroundColor: '#e8f5e9',
                borderColor: COLORS.success,
                color: '#2e7d32',
                borderRadius: '8px',
                borderLeft: `4px solid ${COLORS.success}`
              }}>
                <div className="d-flex align-items-center">
                  <i className="bi bi-check-circle me-2"></i>
                  <div className="flex-grow-1">
                    {infoMsg}
                    <div className="mt-2 small" style={{ color: '#1b5e20' }}>
                      <div className="spinner-border spinner-border-sm text-success me-2" role="status">
                        <span className="visually-hidden">Chargement...</span>
                      </div>
                      Redirection vers la liste des produits...
                    </div>
                  </div>
                  <button type="button" className="btn-close" onClick={() => setInfoMsg("")}></button>
                </div>
              </div>
            )}
            
            <form onSubmit={handleSubmit}>
              <div className="row">
                {/* Sélection du produit avec recherche */}
                <div className="col-12 mb-4" ref={dropdownRef}>
                  <label className="form-label fw-bold mb-3" style={{ 
                    color: COLORS.darkText,
                    fontSize: '0.95rem'
                  }}>
                   <span className="text-danger">*</span> Produit :
                  </label>
                  
                  <div className="position-relative">
                    <div className="input-group">
                      <span className="input-group-text" style={{
                        backgroundColor: COLORS.lightBg,
                        borderColor: '#e0e0e0',
                        color: COLORS.secondary
                      }}>
                        <i className="bi bi-search"></i>
                      </span>
                      <input
                        type="text"
                        className="form-control"
                        id="produit-search"
                        placeholder="Rechercher un produit avec stock disponible..."
                        value={produitSearch}
                        onChange={handleProduitSearchChange}
                        onFocus={() => setShowProduitDropdown(true)}
                        disabled={loading || loadingProducts || produits.length === 0}
                        required
                        style={{
                          borderColor: '#ced4da',
                          padding: '10px 12px',
                          fontSize: '0.95rem',
                          backgroundColor: COLORS.white,
                          transition: 'all 0.3s ease'
                        }}
                      />
                      {formData.id_produit && (
                        <button
                          type="button"
                          className="btn btn-outline-danger"
                          onClick={() => {
                            setFormData({ ...formData, id_produit: "" });
                            setProduitSearch("");
                          }}
                          title="Effacer la sélection"
                          disabled={loading || loadingProducts}
                        >
                          <i className="bi bi-x"></i>
                        </button>
                      )}
                    </div>
                    
                    {showProduitDropdown && !loadingProducts && produits.length > 0 && (
                      <div className="dropdown-menu show w-100" style={{ 
                        maxHeight: "250px", 
                        overflowY: "auto",
                        zIndex: 1050,
                        borderRadius: '8px',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                        border: 'none'
                      }}>
                        {filteredProduits.length > 0 ? (
                          filteredProduits.map((produit) => (
                            <button
                              key={produit.value}
                              type="button"
                              className={`dropdown-item d-flex justify-content-between align-items-center ${formData.id_produit === produit.value ? 'bg-primary text-white' : ''}`}
                              onClick={() => handleProduitSelect(produit.value, produit.label)}
                              style={{ padding: '10px 16px' }}
                            >
                              <div>
                                <div className="fw-bold" style={{ fontSize: "0.95rem" }}>{produit.libele}</div>
                                <small className={formData.id_produit === produit.value ? 'text-light' : 'text-muted'} style={{ fontSize: "0.8rem" }}>
                                  Stock disponible: {produit.stock}
                                </small>
                              </div>
                              {formData.id_produit === produit.value && (
                                <i className="bi bi-check-lg"></i>
                              )}
                            </button>
                          ))
                        ) : (
                          <div className="dropdown-item text-muted text-center py-3">
                            <i className="bi bi-search me-2"></i>
                            Aucun produit trouvé
                          </div>
                        )}
                      </div>
                    )}
                    
                    {formData.id_produit && produits.length > 0 && (
                      <div className="mt-3">
                        <span className="badge fw-semibold" style={{ 
                          backgroundColor: '#E3F2FD',
                          color: COLORS.primary,
                          padding: '6px 12px',
                          borderRadius: '6px',
                          fontSize: '0.85rem'
                        }}>
                          <i className="bi bi-check-circle me-1"></i>
                          Sélectionné: {produits.find(p => p.value === formData.id_produit)?.libele}
                        </span>
                      </div>
                    )}
                  </div>
                  
                  {loadingProducts && (
                    <div className="form-text mt-2 d-flex align-items-center" style={{ color: '#6c757d', fontSize: '0.85rem' }}>
                      <div className="spinner-border spinner-border-sm me-2" role="status" style={{ color: COLORS.primary }}>
                        <span className="visually-hidden">Chargement...</span>
                      </div>
                      Chargement des produits...
                    </div>
                  )}
                  
                  {produits.length === 0 && !loadingProducts && !abonnementError && (
                    <div className="form-text d-flex align-items-center mt-2" style={{ color: COLORS.warning, fontSize: '0.85rem' }}>
                      <i className="bi bi-exclamation-triangle me-2"></i>
                      Aucun produit actif avec stock disponible.
                    </div>
                  )}
                  
                  {produits.length > 0 && !loadingProducts && (
                    <div className="form-text mt-2 d-flex align-items-center" style={{ color: '#6c757d', fontSize: '0.85rem' }}>
                      <i className="bi bi-info-circle me-2"></i>
                      <span>
                        {produits.length} produit{produits.length > 1 ? 's' : ''} disponible{produits.length > 1 ? 's' : ''}
                        {produitSearch && filteredProduits.length > 0 && (
                          <span className="ms-2">
                            ({filteredProduits.length} résultat{filteredProduits.length > 1 ? 's' : ''})
                          </span>
                        )}
                      </span>
                    </div>
                  )}
                  
                  {selectedProduit && (
                    <div className="alert alert-dismissible fade show mt-3" style={{ 
                      backgroundColor: '#E3F2FD',
                      borderColor: COLORS.info,
                      color: COLORS.primary,
                      borderRadius: '8px',
                      borderLeft: `4px solid ${COLORS.info}`,
                      padding: '12px 16px'
                    }}>
                      <div className="d-flex align-items-center">
                        <i className="bi bi-box-seam me-2 fs-5"></i>
                        <div>
                          <strong>Stock actuel:</strong>{" "}
                          <span className="badge fw-semibold ms-2" style={{ 
                            backgroundColor: COLORS.info,
                            color: COLORS.white,
                            padding: '4px 10px',
                            borderRadius: '4px',
                            fontSize: '0.85rem'
                          }}>
                            {selectedProduit.stock} unités
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Quantité à retirer */}
                <div className="col-12 mb-4">
                  <label htmlFor="quantite_sortie_stock" className="form-label fw-bold mb-3" style={{ 
                    color: COLORS.darkText,
                    fontSize: '0.95rem'
                  }}>
                  <span className="text-danger">*</span>  Quantité à retirer :
                  </label>
                  <div className="input-group">
                    <span className="input-group-text" style={{
                      backgroundColor: COLORS.lightBg,
                      borderColor: '#e0e0e0',
                      color: COLORS.danger
                    }}>
                      <i className="bi bi-dash-lg"></i>
                    </span>
                    <input
                      type="number"
                      className="form-control"
                      id="quantite_sortie_stock"
                      name="quantite_sortie_stock"
                      value={formData.quantite_sortie_stock}
                      onChange={handleChange}
                      placeholder="Ex: 10"
                      min="1"
                      max={currentStock}
                      disabled={loading || loadingProducts || produits.length === 0 || !formData.id_produit}
                      required
                      style={{
                        borderColor: '#ced4da',
                        borderRadius: '0 6px 6px 0',
                        padding: '10px 12px',
                        fontSize: '0.95rem',
                        backgroundColor: COLORS.white,
                        transition: 'all 0.3s ease'
                      }}
                    />
                  </div>
                  <div className="form-text mt-2" style={{ color: '#6c757d', fontSize: '0.85rem' }}>
                    <i className="bi bi-info-circle me-1"></i>
                    Entrez la quantité à retirer du stock existant. Maximum: {currentStock} unités
                  </div>
                  
                  {quantiteSortie > 0 && (
                    <div className={`alert alert-dismissible fade show mt-3 ${nouveauStock === 0 ? 'alert-danger' : nouveauStock < 10 ? 'alert-warning' : 'alert-info'}`}
                      style={{ 
                        backgroundColor: nouveauStock === 0 ? '#ffebee' : nouveauStock < 10 ? '#fff3e0' : '#E3F2FD',
                        borderColor: nouveauStock === 0 ? COLORS.danger : nouveauStock < 10 ? COLORS.warning : COLORS.info,
                        color: nouveauStock === 0 ? '#c62828' : nouveauStock < 10 ? '#f57c00' : COLORS.primary,
                        borderRadius: '8px',
                        borderLeft: `4px solid ${nouveauStock === 0 ? COLORS.danger : nouveauStock < 10 ? COLORS.warning : COLORS.info}`,
                        padding: '12px 16px'
                      }}>
                      <div className="d-flex align-items-center">
                        <i className={`bi me-2 fs-5 ${nouveauStock === 0 ? 'bi-exclamation-triangle' : nouveauStock < 10 ? 'bi-exclamation-triangle' : 'bi-calculator'}`}></i>
                        <div>
                          <strong>Nouveau stock après sortie:</strong>{" "}
                          <span className="ms-2">
                            {currentStock} - {quantiteSortie} ={" "}
                            <strong className={nouveauStock === 0 ? 'text-danger' : nouveauStock < 10 ? 'text-warning' : 'text-success'} 
                              style={{ fontSize: '1.1rem' }}>
                              {nouveauStock}
                            </strong> unités
                          </span>
                          {nouveauStock === 0 && (
                            <div className="mt-2 small fw-bold">
                              <i className="bi bi-exclamation-triangle me-1"></i>
                              Le produit sera en rupture de stock
                            </div>
                          )}
                          {nouveauStock < 10 && nouveauStock > 0 && (
                            <div className="mt-2 small fw-bold">
                              <i className="bi bi-exclamation-triangle me-1"></i>
                              Le stock sera faible
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Motif de sortie */}
                <div className="col-12 mb-4">
                  <label htmlFor="motif_sortie_stock" className="form-label fw-bold mb-3" style={{ 
                    color: COLORS.darkText,
                    fontSize: '0.95rem'
                  }}>
                   <span className="text-danger">*</span> Motif de la sortie :
                  </label>
                  <textarea
                    className="form-control"
                    id="motif_sortie_stock"
                    name="motif_sortie_stock"
                    value={formData.motif_sortie_stock}
                    onChange={handleChange}
                    placeholder="Décrivez la raison de cette sortie (ex: Livraison au site principal, Équipement pour agent, etc.)"
                    rows="3"
                    disabled={loading}
                    required
                    style={{
                      borderColor: '#ced4da',
                      borderRadius: '6px',
                      padding: '10px 12px',
                      fontSize: '0.95rem',
                      backgroundColor: COLORS.white,
                      transition: 'all 0.3s ease'
                    }}
                  />
                  <div className="form-text mt-2" style={{ color: '#6c757d', fontSize: '0.85rem' }}>
                    <i className="bi bi-info-circle me-1"></i>
                    Description obligatoire de la raison de la sortie (max 255 caractères)
                  </div>
                </div>
                
                {/* Type de destination */}
                <div className="col-md-6 mb-4">
                  <label htmlFor="destination_type" className="form-label fw-bold mb-3" style={{ 
                    color: COLORS.darkText,
                    fontSize: '0.95rem'
                  }}>
                  <span className="text-danger">*</span> Destination :
                  </label>
                  <select
                    className="form-select"
                    id="destination_type"
                    name="destination_type"
                    value={formData.destination_type}
                    onChange={handleChange}
                    disabled={loading}
                    required
                    style={{
                      borderColor: '#ced4da',
                      borderRadius: '6px',
                      padding: '10px 12px',
                      fontSize: '0.95rem',
                      backgroundColor: COLORS.white,
                      transition: 'all 0.3s ease'
                    }}
                  >
                    <option value="">Sélectionnez une destination</option>
                    {destinationTypes.map((type) => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                  <div className="form-text mt-2" style={{ color: '#6c757d', fontSize: '0.85rem' }}>
                    <i className="bi bi-info-circle me-1"></i>
                    Sélectionnez qui recevra le matériel
                  </div>
                </div>
                
                {/* ID de destination */}
                <div className="col-md-6 mb-4">
                  <label htmlFor="destination_id" className="form-label fw-bold mb-3" style={{ 
                    color: COLORS.darkText,
                    fontSize: '0.95rem'
                  }}>
                    {formData.destination_type && formData.destination_type !== 'self' ? (
                      <>
                     <span className="text-danger">*</span> {formData.destination_type === 'site' ? 'Site' : 
                           formData.destination_type === 'agent' ? 'Agent' : 
                           'Contrôleur'} :
                      </>
                    ) : (
                      "Destination :"
                    )}
                  </label>
                  <select
                    className="form-select"
                    id="destination_id"
                    name="destination_id"
                    value={formData.destination_id}
                    onChange={handleChange}
                    disabled={loading || loadingDestinations || 
                    !formData.destination_type || 
                    formData.destination_type === 'self' ||
                    destinations.length === 0}
                    required={formData.destination_type && formData.destination_type !== 'self'}
                    style={{
                      borderColor: '#ced4da',
                      borderRadius: '6px',
                      padding: '10px 12px',
                      fontSize: '0.95rem',
                      backgroundColor: COLORS.white,
                      transition: 'all 0.3s ease'
                    }}
                  >
                    <option value="">
                      {loadingDestinations ? 'Chargement...' : 
                       !formData.destination_type ? 'Sélectionnez d\'abord un type' :
                       formData.destination_type === 'self' ? `Vous-même (${currentUser ? (currentUser.profile === 1 ? 'administrateur' : 'gestionnaire') : 'utilisateur'})` :
                       destinations.length === 0 ? 'Aucune destination disponible' :
                       `Sélectionnez ${formData.destination_type === 'site' ? 'un site' : 
                        formData.destination_type === 'agent' ? 'un agent' : 
                        'un contrôleur'}`}
                    </option>
                    {destinations.map((dest) => (
                      <option key={dest.value} value={dest.value}>
                        {dest.label}
                      </option>
                    ))}
                  </select>
                  {loadingDestinations && (
                    <div className="form-text mt-2 d-flex align-items-center" style={{ color: '#6c757d', fontSize: '0.85rem' }}>
                      <div className="spinner-border spinner-border-sm me-2" role="status" style={{ color: COLORS.primary }}>
                        <span className="visually-hidden">Chargement...</span>
                      </div>
                      Chargement des destinations...
                    </div>
                  )}
                  {formData.destination_type === 'self' && currentUser && (
                    <div className="form-text mt-2 d-flex align-items-center" style={{ color: COLORS.info, fontSize: '0.85rem' }}>
                      <i className="bi bi-person-check me-1"></i>
                      Le matériel vous sera attribué (en tant que {currentUser.profile === 1 ? 'administrateur' : 'gestionnaire'})
                    </div>
                  )}
                </div>
                
                {/* Incident associé avec recherche */}
                <div className="col-12 mb-4" ref={incidentDropdownRef}>
                  <label className="form-label fw-bold mb-3" style={{ 
                    color: COLORS.darkText,
                    fontSize: '0.95rem'
                  }}>
                    Incident associé (optionnel) :
                  </label>
                  
                  <div className="position-relative">
                    <div className="input-group">
                      <span className="input-group-text" style={{
                        backgroundColor: COLORS.lightBg,
                        borderColor: '#e0e0e0',
                        color: COLORS.secondary
                      }}>
                        <i className="bi bi-search"></i>
                      </span>
                      <input
                        type="text"
                        className="form-control"
                        id="incident-search"
                        placeholder="Rechercher un incident par ticket ou description..."
                        value={incidentSearch}
                        onChange={handleIncidentSearchChange}
                        onFocus={() => setShowIncidentDropdown(true)}
                        disabled={loading || loadingIncidents}
                        style={{
                          borderColor: '#ced4da',
                          borderRadius: '0 6px 6px 0',
                          padding: '10px 12px',
                          fontSize: '0.95rem',
                          backgroundColor: COLORS.white,
                          transition: 'all 0.3s ease'
                        }}
                      />
                      {formData.id_incident && (
                        <button
                          type="button"
                          className="btn btn-outline-danger"
                          onClick={() => {
                            setFormData({ ...formData, id_incident: "" });
                            setIncidentSearch("");
                          }}
                          title="Effacer la sélection"
                          disabled={loading || loadingIncidents}
                        >
                          <i className="bi bi-x"></i>
                        </button>
                      )}
                    </div>
                    
                    {showIncidentDropdown && !loadingIncidents && incidents.length > 0 && (
                      <div className="dropdown-menu show w-100" style={{ 
                        maxHeight: "250px", 
                        overflowY: "auto",
                        zIndex: 1050,
                        borderRadius: '8px',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                        border: 'none'
                      }}>
                        {filteredIncidents.length > 0 ? (
                          filteredIncidents.map((incident) => (
                            <button
                              key={incident.value}
                              type="button"
                              className={`dropdown-item d-flex flex-column ${formData.id_incident === incident.value ? 'bg-primary text-white' : ''}`}
                              onClick={() => handleIncidentSelect(incident.value, incident.label)}
                              style={{
                                padding: '10px 16px',
                                textAlign: 'left'
                              }}
                            >
                              <div className="d-flex justify-content-between align-items-start w-100">
                                <div>
                                  <div className="fw-bold" style={{ fontSize: "0.9rem" }}>
                                    Ticket {incident.numero_ticket}
                                  </div>
                                  <div className={formData.id_incident === incident.value ? 'text-light' : 'text-muted'} 
                                    style={{ fontSize: "0.8rem" }}>
                                    {incident.description}
                                  </div>
                                </div>
                                {formData.id_incident === incident.value && (
                                  <i className="bi bi-check-lg ms-2"></i>
                                )}
                              </div>
                            </button>
                          ))
                        ) : (
                          <div className="dropdown-item text-muted text-center py-3">
                            <i className="bi bi-search me-2"></i>
                            Aucun incident trouvé
                          </div>
                        )}
                      </div>
                    )}
                    
                    {formData.id_incident && incidents.length > 0 && (
                      <div className="mt-3">
                        <span className="badge fw-semibold" style={{ 
                          backgroundColor: '#E3F2FD',
                          color: COLORS.primary,
                          padding: '6px 12px',
                          borderRadius: '6px',
                          fontSize: '0.85rem'
                        }}>
                          <i className="bi bi-check-circle me-1"></i>
                          Sélectionné: Ticket {selectedIncident?.numero_ticket}
                        </span>
                      </div>
                    )}
                  </div>
                  
                  {loadingIncidents && (
                    <div className="form-text mt-2 d-flex align-items-center" style={{ color: '#6c757d', fontSize: '0.85rem' }}>
                      <div className="spinner-border spinner-border-sm me-2" role="status" style={{ color: COLORS.primary }}>
                        <span className="visually-hidden">Chargement...</span>
                      </div>
                      Chargement des incidents...
                    </div>
                  )}
                  
                  {incidents.length === 0 && !loadingIncidents && !abonnementError && (
                    <div className="form-text d-flex align-items-center mt-2" style={{ color: COLORS.warning, fontSize: '0.85rem' }}>
                      <i className="bi bi-exclamation-triangle me-2"></i>
                      Il n'y a pas d'incident non résolu
                    </div>
                  )}
                  
                  {incidents.length > 0 && !loadingIncidents && (
                    <div className="form-text mt-2 d-flex align-items-center" style={{ color: '#6c757d', fontSize: '0.85rem' }}>
                      <i className="bi bi-info-circle me-2"></i>
                      <span>
                        {incidents.length} incident{incidents.length > 1 ? 's' : ''} non résolu{incidents.length > 1 ? 's' : ''} disponible{incidents.length > 1 ? 's' : ''}
                        {incidentSearch && filteredIncidents.length > 0 && (
                          <span className="ms-2">
                            ({filteredIncidents.length} résultat{filteredIncidents.length > 1 ? 's' : ''})
                          </span>
                        )}
                      </span>
                    </div>
                  )}
                  
                  {selectedIncident && (
                    <div className="alert alert-info alert-dismissible fade show mt-3" style={{ 
                      backgroundColor: '#E3F2FD',
                      borderColor: COLORS.info,
                      color: COLORS.primary,
                      borderRadius: '8px',
                      borderLeft: `4px solid ${COLORS.info}`,
                      padding: '12px 16px'
                    }}>
                      <div className="d-flex align-items-start">
                        <i className="bi bi-ticket-detailed me-2 fs-5 mt-1"></i>
                        <div className="flex-grow-1">
                          <div className="d-flex justify-content-between align-items-start">
                            <div>
                              <strong>Incident sélectionné:</strong>
                              <div className="mt-1">
                                <div><strong>Ticket:</strong> {selectedIncident.numero_ticket}</div>
                                <div><strong>Description:</strong> {selectedIncident.description}</div>
                                <div><strong>Statut:</strong> {selectedIncident.statut}</div>
                                <div><strong>Date de résolution:</strong> {selectedIncident.date_resolus_incident ? selectedIncident.date_resolus_incident : 'Non résolu'}</div>
                              </div>
                            </div>
                            <button 
                              type="button" 
                              className="btn-close" 
                              onClick={() => {
                                setFormData({...formData, id_incident: ""});
                                setIncidentSearch("");
                              }}
                            ></button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              
              <div className="mt-5 pt-4 border-top" style={{ borderColor: '#e0e0e0' }}>
                <div className="d-flex justify-content-end gap-3">
                  <button
                    type="button"
                    className="btn fw-semibold"
                    onClick={handleCancel}
                    disabled={loading || loadingProducts}
                    style={{
                      color: COLORS.primary,
                      borderColor: COLORS.primary,
                      backgroundColor: 'transparent',
                      borderRadius: '8px',
                      padding: '10px 24px',
                      minWidth: '120px',
                      transition: 'all 0.3s ease'
                    }}
                    onMouseEnter={(e) => {
                      if (!loading && !loadingProducts) {
                        e.target.style.backgroundColor = '#E3F2FD';
                        e.target.style.borderColor = COLORS.secondary;
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!loading && !loadingProducts) {
                        e.target.style.backgroundColor = 'transparent';
                        e.target.style.borderColor = COLORS.primary;
                      }
                    }}
                  >
                    <i className="bi bi-x-lg me-2"></i>
                    Annuler
                  </button>
                  
                  <button
                    type="submit"
                    className="btn fw-semibold"
                    disabled={loading || loadingProducts || produits.length === 0 || !formData.id_produit}
                    style={{
                      color: COLORS.white,
                      backgroundColor: currentStock === 0 ? '#B0BEC5' : COLORS.primary,
                      borderColor: currentStock === 0 ? '#B0BEC5' : COLORS.primary,
                      borderRadius: '8px',
                      padding: '10px 24px',
                      minWidth: '180px',
                      transition: 'all 0.3s ease'
                    }}
                    onMouseEnter={(e) => {
                      if (!loading && currentStock > 0) {
                        e.target.style.backgroundColor = '#0A3250';
                        e.target.style.borderColor = '#0A3250';
                        e.target.style.transform = 'translateY(-2px)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!loading && currentStock > 0) {
                        e.target.style.backgroundColor = COLORS.primary;
                        e.target.style.borderColor = COLORS.primary;
                        e.target.style.transform = 'translateY(0)';
                      }
                    }}
                  >
                    {loading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                        Sortie en cours...
                      </>
                    ) : (
                      <>
                        <i className="bi bi-box-arrow-up me-2 fw-bold"></i>
                        Effectuer la sortie
                      </>
                    )}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default SortirStockCreate;